import { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate, useMatch, useLocation } from 'react-router-dom';
import { getPathName, ROUTES } from '@openclassrooms/routing';
import Box from '@classify/react-core-components/Box';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import { MetaGlobal } from '@openclassrooms/utils/page-header/components';
import { styled } from '@classify/react-core-components/styles';

import CourseChapterHeader from '../components/CourseChapterHeader';
import ChapterCompletionBanner from '../components/ChapterCompletionBanner';
import CourseEditionPanel from '../components/CourseEditionPanel';
import CourseChapterContext from '../contexts/courseChapterContext';
import CourseContext from '../contexts/courseContext';
import TocToggler from '../components/TableOfContent/TocToggler';
import EditionPanelVariants from '../constants/EditionPanelVariants';
import CourseNeedLogin from '../components/TrainingMerchandising/CourseNeedLogin';
import CourseTitle from '../components/CourseTitle';
import CourseFooterWrapper from '../components/CourseFooterWrapper';
import ChapterContentWrapper from '../components/ChapterContentWrapper';
import getCourseJsonLD from '../services/getCourseJsonLD';

const CourseContentWrapper = styled(ContentWrapper)(({ theme: { spacing } }) => ({
  position: 'relative',
  padding: spacing(4, 2),
  width: 'fill-available',

  '& p a, & p code': {
    wordBreak: 'break-word',
  },
}));

const LoginBannerWrapper = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  margin: spacing(2, 0),
  [breakpoints.up('md')]: {
    margin: spacing(4, 0),
  },
}));

const CourseChapterScreen = () => {
  const { search } = useLocation();
  const location = useLocation();
  const match = useMatch(ROUTES.COURSES.CHAPTERS);
  const navigate = useNavigate();
  const headerRef = useRef(null);

  const [courseId, courseSlug] = match.params.courseIdSlug.split(/-(.+)/);
  const [chapterId, chapterSlug] = match.params.chapterIdSlug.split(/-(.+)/);

  const [hasRedirected, setHasRedirected] = useState(false);

  const [isTocOpen, setIsTocOpen] = useState(true);

  const [hasCancelledCompletion, setHasCancelledCompletion] = useState(false);

  const { course, currentUser, canEditCourse, userToc } = useContext(CourseContext);
  const { currentChapter } = useContext(CourseChapterContext);

  useEffect(() => {
    if (currentChapter) {
      const isUrlSlugIncorrect =
        course &&
        currentChapter &&
        (courseSlug !== course.slug || chapterSlug !== currentChapter.slug);
      const searchParams = new URLSearchParams(search);
      const courseStatus = searchParams.get('status');

      if (isUrlSlugIncorrect && !courseStatus) {
        navigate(
          getPathName(
            `${match.pattern.path}?${location.search}`,
            {
              courseIdSlug: `${courseId}-${course.slug}`,
              chapterIdSlug: `${chapterId}-${currentChapter.slug}`,
            },
            false
          ),
          { replace: true }
        );
        setHasRedirected(true);
      }
    }
  }, [
    course,
    courseId,
    courseSlug,
    currentChapter,
    location.search,
    chapterId,
    chapterSlug,
    navigate,
    match.pattern.path,
    search,
  ]);

  const shouldShowCompletionBanner = currentChapter?.isCompleted || hasCancelledCompletion;

  const jsonLD = getCourseJsonLD(course, userToc);

  return (
    <>
      <MetaGlobal title={currentChapter?.title} imageUrl={course?.illustration} jsonLD={jsonLD}>
        {hasRedirected && <meta name="prerender-status-code" content="302" />}
      </MetaGlobal>

      <div ref={headerRef}>
        <CourseChapterHeader />

        {shouldShowCompletionBanner && (
          <ChapterCompletionBanner onClick={() => setHasCancelledCompletion(true)} />
        )}
      </div>

      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          pb: 2,
        }}
      >
        <TocToggler
          headerRef={headerRef}
          onItemClick={() => setHasCancelledCompletion(false)}
          isOpen={isTocOpen}
          setIsOpen={setIsTocOpen}
        />

        <CourseContentWrapper variant="narrow">
          {canEditCourse && (
            <CourseEditionPanel
              courseId={courseId}
              chapterId={chapterId}
              isPublished={Boolean(course?.publishedAt)}
              variant={EditionPanelVariants.CHAPTER}
            />
          )}

          <CourseTitle>{currentChapter?.title}</CourseTitle>

          {!currentUser && (
            <LoginBannerWrapper>
              <CourseNeedLogin />
            </LoginBannerWrapper>
          )}

          <ChapterContentWrapper courseId={courseId} chapterId={chapterId} />
        </CourseContentWrapper>
      </Box>

      <CourseFooterWrapper />
    </>
  );
};

export default CourseChapterScreen;
